import { useEffect, useState } from "react";
import { X, Loader2, CheckCircle2, BadgeCheck, QrCode, CreditCard } from "lucide-react";
import QRCode from "qrcode";
import { api } from "../lib/api";
import { useEscapeKey } from "../lib/useEscapeKey";

/**
 * v3.9.42 (Blueprint Stage 1) — subscribe to a catalog plan. Free plans activate
 * instantly; paid plans pay via GMPay (QRPH/card) with settlement polling, reusing
 * the same rails as the wallet. Design matches the existing modal components.
 */
import { formatPhp } from "../lib/money";
const peso = formatPhp;

export function SubscribeModal({
  open, onClose, plan, onDone,
}: {
  open: boolean;
  onClose: () => void;
  plan: { key: string; name: string; price: number; interval?: string } | null;
  onDone: () => void;
}) {
  const [method, setMethod] = useState<"qrph" | "card">("qrph");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [qrImg, setQrImg] = useState<string | null>(null);
  const [pending, setPending] = useState<null | { text: string; url?: string }>(null);
  const [done, setDone] = useState(false);
  const [checking, setChecking] = useState(false);
  const [paused, setPaused] = useState(false);
  useEscapeKey(onClose, open && !busy && !checking);

  useEffect(() => { if (open) { setBusy(false); setErr(null); setQrImg(null); setPending(null); setDone(false); setPaused(false); } }, [open, plan?.key]);

  useEffect(() => {
    if (!open || !pending || !plan) return;
    let alive = true;
    const t = setInterval(async () => {
      const s = await api.myPlan().catch(() => null);
      if (!alive || !s) return;
      const active = s.sections?.some((x: any) => x.plan?.key === plan.key && x.isPaid);
      if (active) { clearInterval(t); setDone(true); }
    }, 4000);
    return () => { alive = false; clearInterval(t); };
  }, [open, pending, plan]);

  if (!open || !plan) return null;
  const free = !plan.price;

  async function pay() {
    setBusy(true); setErr(null); setQrImg(null); setPending(null);
    try {
      const r = await api.subscribePlan({ planKey: plan!.key, method });
      if (r?.status === "completed") { setDone(true); return; }
      if (method === "qrph" && r?.qrRaw) {
        setQrImg(await QRCode.toDataURL(r.qrRaw, { width: 220, margin: 1, errorCorrectionLevel: "H" }));
        setPending({ text: "Scan this QRPH code to pay. Your plan activates once GMPay confirms." });
      } else if (method === "card" && r?.checkoutUrl) {
        setPending({ text: "Complete your card payment with GMPay. Your plan activates once confirmed.", url: r.checkoutUrl });
        try { window.open(r.checkoutUrl, "_blank", "noopener"); } catch { /* ignore */ }
      } else setPending({ text: "Payment started — your plan activates once GMPay confirms." });
    } catch (failure: unknown) {
      if (failure instanceof Error && "code" in failure && failure.code === "paid_plans_unavailable") setPaused(true);
      else setErr(failure instanceof Error ? failure.message : "Could not start the payment.");
    }
    finally { setBusy(false); }
  }
  async function checkNow() {
    setChecking(true);
    try {
      const s = await api.myPlan().catch(() => null);
      if (s?.sections?.some((x: any) => x.plan?.key === plan!.key && x.isPaid)) setDone(true);
      else setErr("Payment not confirmed yet — give it a few seconds and check again.");
    } finally { setChecking(false); }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={onClose}>
      <div className="card w-full max-w-md p-0 overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="px-4 py-3 border-b border-line flex items-center gap-2">
          <BadgeCheck className="w-4 h-4 text-navy" />
          <h3 className="font-bold text-navy">{plan.name}</h3>
          <button onClick={onClose} aria-label="Close" className="btn-ghost h-8 w-8 p-0 ml-auto"><X className="w-4 h-4" /></button>
        </div>

        {paused ? (
          <div className="p-6 text-center">
            <p className="font-semibold text-navy">Paid plans are coming soon</p>
            <p className="text-sm text-mut mt-1">You can continue posting for free.</p>
            <button onClick={onClose} className="btn-navy h-9 px-4 text-sm mt-4">Continue</button>
          </div>
        ) : done ? (
          <div className="p-6 text-center">
            <CheckCircle2 className="w-10 h-10 text-brand-green mx-auto mb-2" />
            <p className="font-semibold text-navy">{free ? "Plan updated" : `You're on ${plan.name}`}</p>
            <p className="text-sm text-mut mt-1">Your entitlements are active now.</p>
            <button onClick={onDone} className="btn-navy h-9 px-4 text-sm mt-4">Done</button>
          </div>
        ) : pending ? (
          <div className="p-6 text-center">
            {qrImg ? <img src={qrImg} alt="GMPay QRPH" className="w-52 h-52 mx-auto rounded-lg border border-line" />
              : <div className="w-14 h-14 rounded-full bg-brand-gold/10 grid place-items-center mx-auto mb-2"><Loader2 className="w-7 h-7 text-brand-gold animate-spin" /></div>}
            <p className="text-sm text-mut mt-3">{pending.text}</p>
            {pending.url && <a href={pending.url} target="_blank" rel="noopener noreferrer" className="text-sm text-brand-red underline">Reopen payment page</a>}
            {err && <p className="text-xs text-brand-red mt-2">{err}</p>}
            <div className="flex gap-2 justify-center mt-4">
              <button onClick={checkNow} disabled={checking} className="btn-navy h-9 px-4 text-sm disabled:opacity-60">{checking ? <Loader2 className="w-4 h-4 animate-spin" /> : "I've paid — check now"}</button>
              <button onClick={onClose} className="btn-outline h-9 px-4 text-sm">Cancel</button>
            </div>
          </div>
        ) : (
          <div className="p-4 space-y-3">
            <div className="text-center">
              <div className="text-3xl font-extrabold text-navy">{free ? "Free" : peso(plan.price)}{!free && plan.interval && <span className="text-sm text-mut font-semibold">/{plan.interval === "year" ? "yr" : "mo"}</span>}</div>
            </div>
            {!free && (
              <div className="flex items-center justify-center gap-2 text-xs">
                <span className="text-mut">Pay with</span>
                <button onClick={() => setMethod("qrph")} className={`chip flex items-center gap-1 ${method === "qrph" ? "bg-navy text-white" : "bg-navy-50 text-navy-600"}`}><QrCode className="w-3 h-3" />QRPH</button>
                <button onClick={() => setMethod("card")} className={`chip flex items-center gap-1 ${method === "card" ? "bg-navy text-white" : "bg-navy-50 text-navy-600"}`}><CreditCard className="w-3 h-3" />Card</button>
              </div>
            )}
            {err && <p className="text-xs text-brand-red">{err}</p>}
            <button onClick={pay} disabled={busy} className="btn-red w-full disabled:opacity-60">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <BadgeCheck className="w-4 h-4" />}{free ? "Switch to this plan" : "Subscribe"}</button>
            <p className="text-[11px] text-mut text-center">{free ? "No payment needed." : "Secured by GMPay. Cancel anytime — your plan runs to the end of the paid period."}</p>
          </div>
        )}
      </div>
    </div>
  );
}
